import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { COMPANY } from "../data/content";
import { FadeUp } from "../components/Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", error: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", error: "" });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email us directly.";
      setStatus({ state: "error", error: detail });
    }
  };

  return (
    <>
      <SEO
        title="Contact — Rudrash Private Limited"
        description="Get in touch with Rudrash Private Limited for partnerships, bulk orders and farm visits at our Kolkata operations."
      />
      <div data-testid="page-contact">
        <PageHero
          eyebrow="Contact"
          title="Let's build something fresh, together."
          subtitle="Whether you are a chef, retailer, export partner or simply curious about our farm — we would love to hear from you."
          image="https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=1800&q=80"
        />

        <section className="py-24 md:py-32 bg-cream">
          <div className="container mx-auto px-6 md:px-10 xl:px-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
            <FadeUp className="lg:col-span-5">
              <p className="eyebrow text-forest">Reach Rudrash</p>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl text-forest leading-[1.1]">
                Kolkata office & farm operations.
              </h2>
              <p className="mt-6 text-muted-ink">
                Weekdays 10:00 — 18:00 IST. Farm visits by appointment.
              </p>

              <ul className="mt-10 space-y-6">
                <li className="flex gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-cream-2 text-saffron shrink-0">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-muted-ink">Address</p>
                    <p className="mt-1 text-forest" data-testid="contact-address">
                      {COMPANY.address}
                    </p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-cream-2 text-saffron shrink-0">
                    <Phone size={18} />
                  </span>
                  <div className="mt-1 text-forest">
                    <a href={COMPANY.phoneHref} className="hover:text-saffron">
                      {COMPANY.phone}
                    </a>
                    <span className="mx-2 text-muted-ink">|</span>
                    <a href={COMPANY.phoneHref2} className="hover:text-saffron">
                      {COMPANY.phone2}
                    </a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-cream-2 text-saffron shrink-0">
                    <Mail size={18} />
                  </span>
                  <div>
                    <p className="eyebrow text-muted-ink">Email</p>
                    <a href={`mailto:${COMPANY.email}`} className="mt-1 block text-forest hover:text-saffron break-all" data-testid="contact-email">
                      {COMPANY.email}
                    </a>
                  </div>
                </li>
              </ul>

              <div className="mt-10 pt-8 border-t border-border-soft">
                <p className="eyebrow text-muted-ink">Corporate Identity</p>
                <p className="mt-2 font-serif text-xl text-forest">CIN · {COMPANY.cin}</p>
              </div>
            </FadeUp>

            <FadeUp delay={0.15} className="lg:col-span-7">
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-cream-2/50 border border-border-soft p-8 md:p-12"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Your name" name="name" value={form.name} onChange={handleChange} required testId="contact-input-name" />
                  <Field label="Email" type="email" name="email" value={form.email} onChange={handleChange} required testId="contact-input-email" />
                </div>
                <div className="mt-6">
                  <Field label="Phone" name="phone" value={form.phone} onChange={handleChange} required testId="contact-input-phone" />
                </div>
                <div className="mt-6">
                  <label className="block eyebrow text-muted-ink mb-3">Message</label>
                  <textarea
                    name="message"
                    required
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    data-testid="contact-input-message"
                    className="w-full bg-cream border border-border-soft focus:border-forest outline-none p-4 text-forest placeholder:text-muted-ink/60 transition-colors resize-none"
                    placeholder="Tell us about your interest, volume, or visit plans..."
                  />
                </div>

                <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <button
                    type="submit"
                    disabled={status.state === "loading"}
                    className="btn-accent disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {status.state === "loading" ? (
                      <>
                        <Loader2 size={14} className="animate-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        Send message <ArrowRight size={14} />
                      </>
                    )}
                  </button>

                  {status.state === "success" && (
                    <div className="flex items-center gap-2 text-forest" data-testid="contact-success">
                      <CheckCircle2 size={18} className="text-saffron" />
                      <span className="text-sm">Thank you — we'll respond shortly.</span>
                    </div>
                  )}
                  {status.state === "error" && (
                    <p className="text-sm text-red-700" data-testid="contact-error">
                      {status.error}
                    </p>
                  )}
                </div>
              </form>
            </FadeUp>
          </div>
        </section>

        {/* Map */}
        <section className="pb-24 bg-cream">
          <div className="container mx-auto px-6 md:px-10 xl:px-16">
            <div className="aspect-[16/7] w-full overflow-hidden border border-border-soft" data-testid="contact-map">
              <iframe
                title="Rudrash Private Limited - Kolkata"
                src="https://www.google.com/maps?q=Sinha+Bhavan+Parbaganj,+Nangl+Bayanagar+LP-205,+Kolkata+-+700141&output=embed"
                width="100%"
                height="100%"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                style={{ border: 0 }}
              />
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

const Field = ({ label, testId, ...rest }) => (
  <div>
    <label className="block eyebrow text-muted-ink mb-3">{label}</label>
    <input
      {...rest}
      data-testid={testId}
      className="w-full bg-cream border border-border-soft focus:border-forest outline-none p-4 text-forest placeholder:text-muted-ink/60 transition-colors"
    />
  </div>
);

export default Contact;
